/**
 * Telemetria de structured blocks descartados (ST-1 / RFC §2.4).
 *
 * Quando o front recebe um block cujo `type` é desconhecido ou cujo shape falha na
 * validação Zod, ele o descarta silenciosamente e cai no `response` textual. Isso é o que
 * torna o contrato extensível — mas sem observabilidade não temos como saber que o backend
 * passou a emitir um shape que o front não entende (drift de contrato). Este helper emite
 * um evento no Datadog RUM para que esses descartes sejam mensuráveis em produção.
 *
 * Substitui o `console.warn` "só em DEV" do spike. O RUM só é inicializado em produção
 * (src/core/config/datadog-rum.ts), então guardamos a chamada pelo mesmo gate para evitar
 * o ruído "addAction called before init" em dev/testes; em DEV mantemos o log de console.
 */
import { datadogRum } from "@datadog/browser-rum";

const isProduction = import.meta.env.VITE_APP_ENVIRONMENT === "production";

interface DroppedBlockInfo {
  /** `type` do block descartado, quando presente (senão "unknown"). */
  blockType: string;
  /** Motivos da falha (issues do Zod) — ajuda a diagnosticar o drift. */
  issues?: unknown;
}

/** Extrai um rótulo de tipo seguro de um block cru (pode nem ser objeto). */
function readBlockType(item: unknown): string {
  if (item && typeof item === "object" && "type" in item) {
    const t = (item as { type?: unknown }).type;
    if (typeof t === "string") return t;
  }
  return "unknown";
}

/**
 * Reporta um block descartado. Nunca lança — telemetria não pode quebrar o render.
 */
export function trackDroppedBlock(item: unknown, issues?: unknown): void {
  const info: DroppedBlockInfo = { blockType: readBlockType(item), issues };

  if (isProduction) {
    try {
      datadogRum.addAction("chatbot_block_discarded", { ...info });
    } catch {
      // no-op: RUM indisponível não deve afetar o chat.
    }
  } else if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      "[blocks] descartado (inválido/desconhecido):",
      info.blockType,
      item,
      issues,
    );
  }
}
